import { ApolloClient } from '@apollo/client';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { showFlash } from '../components-v2/Flash';
import { VersionsContext } from '../contexts';
import { useVersionsQuery } from '../graphql/queries';
import { usePrevious } from '../hooks/usePrevious';
import { useObject } from '../lib/hooks';

const POLL_INTERVAL = 1000;

interface Props {
  client: ApolloClient<unknown>;
  children: ReactNode;
}

export default function VersionsProvider({ client, children }: Props) {
  const { data, loading, startPolling, stopPolling, refetch } = useVersionsQuery();

  const { isSavingCurrentVersion, isRestoringVersion, latestStatus, latestType } =
    data?.versions.status ?? {};

  const isSavingOrRestoring = Boolean(isSavingCurrentVersion || isRestoringVersion);
  const wasSavingCurrentVersion = usePrevious(isSavingCurrentVersion);
  const wasSavingOrRestoring = usePrevious(isSavingOrRestoring);
  const [isRefetching, setIsRefetching] = useState(false);

  const poll = useCallback(() => {
    refetch();
    startPolling(POLL_INTERVAL);
  }, [startPolling, refetch]);

  useEffect(() => {
    if (!isSavingOrRestoring && wasSavingOrRestoring) {
      const wasSuccessful = latestStatus === 'successful';
      const message = wasSuccessful
        ? `Version successfully ${latestType}d`
        : `Could not ${latestType} version`;

      showFlash({ type: wasSuccessful ? 'success' : 'error', message });

      if (latestType === 'restore') {
        setIsRefetching(true);
        client.resetStore().finally(() => {
          setIsRefetching(false);
        });
      }
    }
  }, [isSavingOrRestoring, wasSavingOrRestoring, client, latestStatus, latestType]);

  useEffect(() => {
    if (!isSavingCurrentVersion && wasSavingCurrentVersion) {
      const wasSuccessful = latestStatus === 'successful';

      const message = wasSuccessful
        ? `Current version successfully saved`
        : `Could not restore the current version`;

      showFlash({ type: wasSuccessful ? 'success' : 'error', message });
    }
  });

  useEffect(() => {
    if (isSavingOrRestoring) {
      poll();
    } else {
      stopPolling();
    }
  }, [poll, stopPolling, isSavingOrRestoring]);

  const versions = data?.versions;
  const contextValue = useObject({
    versions,
    loading,
    poll,
    isSavingOrRestoring: isSavingOrRestoring || isRefetching, // we need to show a loading state while queries are re-fetched post-restore
    refetch,
    latestType,
  });

  return <VersionsContext.Provider value={contextValue}>{children}</VersionsContext.Provider>;
}
